import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiDollarSign, FiClock, FiTrendingUp, FiEye, FiCalendar, FiShield } = FiIcons;

const BenefitsSection = ({ onBooking }) => {
  const benefits = [
    {
      icon: FiEye,
      title: "รู้จักโครงสร้างดวงตัวเอง",
      description: "เข้าใจจุดแข็ง จุดอ่อนด้านการเงินของตัวเองอย่างชัดเจน",
      value: "ประหยัดเวลาลองผิดลองถูก หลายปี"
    },
    {
      icon: FiClock,
      title: "รู้ช่วงเวลาที่เหมาะสม",
      description: "ทราบว่าช่วงไหนควรลงทุน ช่วงไหนควรระวัง",
      value: "หลีกเลี่ยงการสูญเสียเงินไปเปล่า"
    },
    {
      icon: FiDollarSign,
      title: "วิธีเรียกทรัพย์เฉพาะตัว",
      description: "ได้วิธีการเสริมดวงการเงินที่คำนวณเฉพาะสำหรับคุณ",
      value: "เพิ่มโอกาสสร้างรายได้ที่เหมาะกับดวงชะตา"
    },
    {
      icon: FiTrendingUp,
      title: "เพิ่มความมั่นใจในการตัดสินใจ",
      description: "มีหลักการชัดเจนในการวางแผนการเงิน",
      value: "ลดความเสี่ยงในการลงทุนและทำธุรกิจ"
    },
    {
      icon: FiCalendar,
      title: "ได้ปฏิทินฤกษ์ประจำตัว",
      description: "รู้วันเวลาที่เหมาะสมสำหรับการทำธุรกรรมสำคัญ",
      value: "เพิ่มโอกาสความสำเร็จในทุกการตัดสินใจ"
    },
    {
      icon: FiShield,
      title: "ยันต์ประจำตัวที่ใช้งานได้จริง",
      description: "ไม่ใช่ยันต์ทั่วไป แต่คำนวณเฉพาะจากดวงชะตาของคุณ (อายุ 1 ปี)",
      value: "เสริมพลังงานบวกด้านการเงินตลอดเวลา"
    }
  ];

  return (
    <section className="py-16 px-4 relative overflow-hidden">
      {/* Enhanced Background decorative image */}
      <div className="absolute inset-0 opacity-25">
        <img 
          src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1751395156128-20240204120138_IMG_1809.JPG" 
          alt="Background" 
          className="absolute top-0 right-0 w-2/3 h-full object-cover rounded-l-3xl" 
        />
        <img 
          src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1751394482125-blob" 
          alt="Background" 
          className="absolute bottom-0 left-0 w-1/2 h-1/2 object-cover rounded-tr-3xl opacity-70" 
        />
      </div>
      
      <div className="container mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            คุณจะได้อะไรจาก<span className="text-amber-400">วิชาปาจื้อเรียกทรัพย์</span>นี้?
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            ไม่ใช่แค่ความรู้ แต่เป็นเครื่องมือที่ใช้งานได้จริงในชีวิตประจำวัน
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto mb-12">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-gradient-to-br from-slate-800/50 to-slate-700/50 backdrop-blur-sm rounded-xl p-6 border border-green-500/20 hover:border-green-500/40 transition-all duration-300 hover:transform hover:scale-105"
            >
              <div className="text-center">
                <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={benefit.icon} className="text-white text-2xl" />
                </div>
                <h3 className="text-lg font-bold text-white mb-3">
                  {benefit.title}
                </h3>
                <p className="text-slate-300 mb-4 text-sm leading-relaxed">
                  {benefit.description}
                </p>
                <div className="bg-green-500/10 rounded-lg p-3 border border-green-500/20">
                  <p className="text-green-400 font-semibold text-sm">
                    💰 คุณค่าที่ได้: {benefit.value}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-green-500/20 to-emerald-600/20 rounded-xl p-8 border border-green-500/30 max-w-4xl mx-auto mb-8">
            <h3 className="text-2xl font-bold text-green-400 mb-4">
              คุณค่าที่แท้จริง
            </h3>
            <p className="text-lg text-white leading-relaxed mb-6">
              หากคิดเป็นเงิน สิ่งที่คุณจะได้รับมีมูลค่ามากกว่า <span className="text-amber-400 font-bold">50,000 บาท</span>
              <br />
              แต่คุณจ่ายเพียง <span className="text-green-400 font-bold">5,500 บาท</span> เท่านั้น
            </p>
            
            {/* Date reminder */}
            <div className="bg-slate-700/40 backdrop-blur-md rounded-lg p-4 mb-6 border border-slate-600/30">
              <p className="text-slate-300 font-medium text-lg">
                📅 วันเรียน: 12 กรกฎาคม 2568 | ⏰ 09.00-17.00 น.<br />
                📍 S Ratchada Leisure Hotel ห้อง Galleria ชั้น 2
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-amber-400 font-semibold">การปรึกษาส่วนตัว</p>
                <p className="text-slate-300">มูลค่า 15,000 บาท</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-amber-400 font-semibold">ยันต์ประจำตัว</p>
                <p className="text-slate-300">มูลค่า 20,000 บาท</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-amber-400 font-semibold">ความรู้ที่ใช้ได้ตลอดชีวิต</p>
                <p className="text-slate-300">ล้ำค่า</p>
              </div>
            </div>
          </div>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onBooking}
            className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-10 py-4 rounded-full text-lg font-bold shadow-2xl hover:shadow-green-500/25 transition-all duration-300"
          >
            รับสิทธิพิเศษเดี๋ยวนี้
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default BenefitsSection;